import * as Sentry from '@sentry/angular';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { Injectable } from '@angular/core';

import {
  BehaviorSubject,
  combineLatest,
  first,
  map,
  Observable,
  of,
  tap,
} from 'rxjs';
import { orderBy } from 'lodash';

import { PUCK_COLORS_HEX, PUCK_COLORS_NAME_PAIR } from '@app/constants/color';
import { EncryptionService } from '@app/modules/encryption/encryption.service';
import { License } from '@app/modules/license/license.types';
import { licenseDeviceUuid } from './transcript/license-device-uuid';
import { Message } from '@app/shared/models/app.model';
import { Microphone } from '../shared/models/microphone.model';
import { ParsedMessage } from '@app/shared/models/parsed-message.model';
import { SettingsService } from './settings.service';
import { TranscriptionCorrectionService } from './transcription-correction.service';
import { TranscriptStreamService } from './transcript/transcript-stream.service';
// import { transcript } from '@app/mock/2-person.mock';

@Injectable({
  providedIn: 'root',
})
/**
 * @name SpeechService class
 * @description This class sources live transcripts from TranscriptStreamService (delivered
 * over WebSocket) and processes them into a message stream and a mic stream for downstream
 * consumers such as the autocaption view.
 */
export class SpeechService {
  private _activeMics: BehaviorSubject<Microphone[]> = new BehaviorSubject([]);
  private _messages: BehaviorSubject<Message[]> = new BehaviorSubject([]);
  private dbData: Observable<Message[]>;
  private staticTextBubbles: Message[] = [];

  private readonly MAX_ACTIVE_MESSAGES = 35; // how many active changes we keep track off
  private readonly CLEAN_UP_MESSAGE_COUNT = 5; // how many changes we make inactive when threshold is reached

  /**
   * Firestore "listen from" cutoff per device, captured on the first listen and
   * reused on every re-subscribe. Without it a reload/re-subscribe pulls the
   * whole recent backlog (latest 25 regardless of age); with it, a page reload
   * starts a fresh service so the map is empty and the cutoff is `Date.now()`,
   * showing only messages from now on.
   */
  private listenCutoffByDb = new Map<string, number>();

  constructor(
    private db: AngularFirestore,
    private settingsService: SettingsService,
    private encryptionService: EncryptionService,
    private correctionService: TranscriptionCorrectionService,
    private transcriptStream: TranscriptStreamService
  ) { }

  get activeMics$(): Observable<Microphone[]> {
    return this._activeMics.asObservable();
  }

  get messages$(): Observable<Message[]> {
    return this._messages.asObservable();
  }

  public async updateMic(id: number, mic: Partial<Microphone>) {
    this.activeMics$
      .pipe(first())
      .subscribe((microphones) => {
        const index = microphones.findIndex((x) => x.id === id);
        if (index !== -1) {
          microphones[index].active = mic?.active ?? microphones[index].active;
          microphones[index].color = mic?.color ?? microphones[index].color;
          microphones[index].name = mic?.name ?? microphones[index].name;
          microphones[index].isMuted =
            mic?.isMuted ?? microphones[index].isMuted;
        }

        this._activeMics.next([...microphones]);
      })
      .unsubscribe();
  }

  /**
   * Returns an observable that triggers on each live transcript snapshot
   * delivered over WebSocket (via TranscriptStreamService; this used to read a
   * Firestore collection). It orders data by ascending timestamp and pushes it
   * into the messages stream.
   *
   * It also checks for a new mic ID and pushes a new mic in stream.
   * If the mic already exists it activates the mic
   */
  public startListener(licenses: License[], outputLanguage?: string, useFirestore = false): Observable<Message[]> {
    this._messages.next([]);
    this._activeMics.next([]);

    const bannedDocumentIDs = new Set<string>();
    const mutedMessageIds = new Set<number>();
    const listeners: Observable<Message[]>[] = [];
    for (const license of licenses) {
      const baseListener = this.buildLicenseListener(license, useFirestore);

      // Apply license-specific corrections before combining streams
      const correctedListener = baseListener.pipe(
        this.correctionService.correctMessagesOperatorForLicense(licenseDeviceUuid(license))
      );

      listeners.push(correctedListener);
    }


    this.dbData = combineLatest(listeners).pipe(
      map((messagesProviders) =>
        messagesProviders.reduce((a, b) => [...a, ...b], [])
      )
    );

    // ------------------------------------------------------------------------
    // NOTE: uncomment if you want fake data
    // TODO: Move out of this class and make it configurable.
    // ------------------------------------------------------------------------
    // const subject = new Subject<Message[]>();
    // const asyncBoy = async () => {
    //   for (const t of transcript) {
    //     subject.next(t);
    //     await new Promise<void>((resolve) => {
    //       setTimeout(() => {
    //         resolve();
    //         // NOTE: This is how we change time
    //       }, 30);
    //     });
    //   }
    // };
    // asyncBoy();
    // this.dbData = subject.asObservable();
    // ------------------------------------------------------------------------

    this.staticTextBubbles = [];
    let changeHistory: ParsedMessage[] = [];
    // (window as any).transcriptStorage = [];
    localStorage.removeItem('transcriptDebug');
    return this.dbData.pipe(
      //enable for storing transcripts for replaying
      // tap((data) => {
      //   let oldData: any[];
      //   try {
      //     oldData = JSON.parse(localStorage.getItem('transcriptDebug')) || [];
      //   } catch {
      //     oldData = [];
      //   }
      //   oldData.push(data);
      //   (window as any).transcriptStorage.push(data);
      //   localStorage.setItem('transcriptDebug', JSON.stringify(oldData));
      // }),
      // Firestore-sourced messages (e.g. hubble microphone kits) don't carry a
      // micColor — the colour lives in the mic settings — so backfill it here
      // from the mic settings (by micId), falling back to a deterministic puck
      // colour. Without this the mic-registration tap below and the bubble /
      // header templates dereference an undefined micColor and throw.
      map((messages: Message[]) => {
        for (const message of messages) {
          if (message.micColor) {
            continue;
          }
          let hex: string | undefined;
          if (typeof message.micId === 'number') {
            try {
              hex = this.settingsService.getMicSettings(message.micId)?.color_hex;
            } catch {
              hex = undefined;
            }
            hex = hex ?? PUCK_COLORS_HEX[Math.abs(message.micId) % PUCK_COLORS_HEX.length];
          }
          hex = hex ?? PUCK_COLORS_HEX[0];
          message.micColor = { hex, name: PUCK_COLORS_NAME_PAIR[hex] ?? '' };
        }
        return messages;
      }),
      tap((messages: Message[]) => {
        console.log('Received WS transcript messages:', JSON.parse(JSON.stringify(messages)));
        const mics = this._activeMics.value;

        for (const message of messages) {
          // Diarized messages carry a string micId (e.g. 'speaker-7') and are
          // not associated with a hardware mic — skip activeMics registration.
          if (typeof message.micId !== 'number') {
            continue;
          }
          const micId = message.micId;
          const existingMic = mics.find((x) => x.id === micId);

          if (!existingMic) {
            try {
              const micSettings = this.settingsService.getMicSettings(micId);
              this._activeMics.next([
                ...mics,
                new Microphone(
                  micSettings.name ||
                  'MIC ' + ((micSettings as any)?.index ?? 0),
                  micSettings.color_hex,
                  micSettings.mic_id,
                  'MIC ' + ((micSettings as any)?.index ?? 0)
                ),
              ]);
            } catch (e) {
              this._activeMics.next([
                ...mics,
                new Microphone(
                  PUCK_COLORS_NAME_PAIR[message.micColor.hex],
                  message.micColor.hex,
                  micId,
                  'MIC ' + micId
                ),
              ]);
            }
          }
        }
      }),
      // ---------------------------------------------------------------------
      // FIX: set `is_encrypted` flag to false by default if it's not set
      // TODO: remove this once all messages includes the `is_encrypted` flag
      map((changes: Message[]) =>
        changes.map((messageUpdate) => {

          messageUpdate.is_encrypted = messageUpdate.is_encrypted ?? false;

          // TODO: weird stuff to make translations work kinda
          if (outputLanguage && outputLanguage !== messageUpdate.language) {
            messageUpdate.message = messageUpdate.transcripts[outputLanguage] ?? messageUpdate.message
          }

          return messageUpdate


        })
      ),
      // ---------------------------------------------------------------------
      map((messages: Message[]) => {
        return messages.filter((message) => {
          // Diarized messages have no hardware mic to mute; pass them through.
          if (typeof message.micId !== 'number') return true;
          const mic = this._activeMics.value.find((m) => m.id === message.micId);

          // If mic is muted, track the message ID and exclude it
          if (mic && mic.isMuted) {
            mutedMessageIds.add(message.timestamp); // Track message as muted
            return false; // Don't include this message
          }

          // If the message has been previously muted, exclude it
          if (mutedMessageIds.has(message.timestamp)) {
            return false;
          }

          // Otherwise, allow the message through
          return true;
        });
      }),
      map((changes: Message[]) => {
        this.updateChangeHistory(changes, changeHistory);
        // TODO: Remove once azure fixes this stuff
        changeHistory = this.removeBannedMessages(changeHistory, bannedDocumentIDs)

        // cleanup crew: once the active set outgrows the tracking cap, archive
        // the oldest already-final messages into staticTextBubbles. This runs
        // BEFORE building the active bubbles and archives by ParsedMessage
        // identity, so an archived message is removed from changeHistory and can
        // never be rendered from both lists (which showed the same message
        // twice once same-speaker merging made the bubble and ParsedMessage
        // counts diverge).
        if (changeHistory.length >= this.MAX_ACTIVE_MESSAGES) {
          const readyToBeStaticBubbles = this.cleanInactiveHistory(
            changeHistory,
            this.CLEAN_UP_MESSAGE_COUNT
          );
          this.staticTextBubbles.push(...readyToBeStaticBubbles);
        }

        // Flatmap the remaining active messages into a single message array
        let singleMessages: Message[] = changeHistory.reduce<Message[]>(
          (result, history) => [...result, ...history.getMessages()],
          []
        );

        // Order everything on update
        singleMessages = orderBy(singleMessages, 'updated_at', 'asc');

        // Combine messages that are consecutively from the same speaker and
        // not final
        const activeTextBubbles = this.generateTextBubbles(
          changeHistory,
          singleMessages
        );

        return [...this.staticTextBubbles, ...activeTextBubbles];
      }),
      tap((messages: Message[]) => {
        this._messages.next(messages);
        return messages;
      })
    );
  }

  private getLastMessageFromSameMic(
    id,
    messages: Message[]
  ): Message | undefined {
    for (let index = messages.length - 1; index >= 0; index--) {
      if (messages[index].documentID === id) {
        return messages[index];
      }
    }
    return undefined;
  }

  /**
   * Takes in a list of messages and loops over them
   * to see if the message should remain separate or combined with a previous message
   *
   * It will combine messages based on the following rules
   * 1. If there was a previous message found for the same mic that was said in the time span of 2 seconds of the new message
   * 2. If the last  message was from the same microphone
   *
   * @param messages all known messages the user received
   * @returns a new list of messages with some messages combined on behavior
   */
  private generateTextBubbles(
    history: ParsedMessage[],
    messages: Message[]
  ): Message[] {
    return messages.reduce((result: Message[], value: Message) => {
      // Don't allow empty things
      if (value.message.trim() === '') {
        return result;
      }

      // last message from same mic/speaker
      const lastSame = this.getLastMessageFromSameMic(value.documentID, result);

      // expand if previous same speaker was still talking (avoid premature
      // splitting)
      if (lastSame) {
        const currentTimestamp = new Date(value.updated_at).getTime();
        const prevTimestamp = new Date(lastSame.updated_at).getTime();
        const elapsed = currentTimestamp - prevTimestamp;
        if (
          !lastSame.is_final &&
          elapsed <=
          (this.settingsService.localSettings.segmentationSilenceTimeout ||
            2500)
        ) {
          lastSame.is_final = value.is_final;
          lastSame.updated_at = value.updated_at;
          lastSame.message += ' ' + value.message;
          return result;
        }
      }

      const lastMessage = result[result.length - 1];
      const lastSpeaker = lastMessage && lastMessage.micId === value.micId;
      if (
        lastSpeaker &&
        (!this.settingsService.localSettings.splitOnFinal ||
          !lastMessage.is_final)
      ) {
        lastMessage.is_final = value.is_final;
        lastMessage.updated_at = value.updated_at;
        lastMessage.message += ' ' + value.message;
      } else {
        result.push(value);
      }

      return result;
    }, []);
  }

  /**
   * Parses the changes received and updates the change history accordingly.
   *
   * @param changes - The array of new changes to be parsed.
   * @param changeHistory - The array of parsed message history.
   */
  private updateChangeHistory(
    changes: Message[],
    changeHistory: ParsedMessage[]
  ) {
    for (const messageUpdate of changes) {
      const history = this.staticTextBubbles.find(
        (change) => change.documentID === messageUpdate.documentID
      );
      if (history) {
        console.log(
          'change was found in history skipping it',
          messageUpdate.documentID
        );
        continue;
      }

      const cachedMessage = changeHistory.find(
        (change) => change.documentID === messageUpdate.documentID
      );

      // Cache a messageUpdate if it's not already cached
      if (!cachedMessage) {
        console.log(
          `Message ${messageUpdate.documentID} not cached, Cacheing it.`
        );
        changeHistory.push(
          new ParsedMessage(
            messageUpdate,
            this.settingsService.localSettings.onlyShowChangesOnFinal || false,
            this.encryptionService
          )
        );
        continue;
      }

      // Update the cachedMessage with the last messageUpdate data
      cachedMessage.addNewChange(messageUpdate);
    }
  }

  /**
   * Archive the oldest `cleanAmount` messages out of the active `changeHistory`
   * once it has grown past the tracking cap, returning them rendered as static
   * bubbles for `staticTextBubbles`.
   *
   * Only archives when those oldest messages are all final (frozen — they will
   * not receive further updates). Archiving splices the ParsedMessages out of
   * `changeHistory` by identity and renders *those* into bubbles, so the same
   * message can never live in both `changeHistory` and `staticTextBubbles` (the
   * previous count-based splice against the merged-bubble array could, which
   * rendered the same message twice).
   */
  private cleanInactiveHistory(
    changeHistory: ParsedMessage[],
    cleanAmount: number
  ): Message[] {
    for (let i = 0; i < cleanAmount; i++) {
      if (!changeHistory[i]?.is_final) {
        console.log('not ready for cleaning yet');
        return [];
      }
    }

    const archived = changeHistory.splice(0, cleanAmount);
    console.log('removing', cleanAmount, archived.map((x) => x.documentID));

    const archivedMessages = orderBy(
      archived.reduce<Message[]>((result, pm) => [...result, ...pm.getMessages()], []),
      'updated_at',
      'asc'
    );
    return this.generateTextBubbles(archived, archivedMessages);
  }

  /**
   * Build one license's transcript stream. Owners (logged-in) read live
   * transcripts from Firestore, which every device writes to. Guests
   * (soundshape QR / shared sessions) have no Firestore access and read from
   * the WebSocket stream instead.
   */
  private buildLicenseListener(license: License, useFirestore: boolean): Observable<Message[]> {
    // Microphone kits driven by hubble (live-transcribe) only ever write to
    // Firestore — they never broadcast on the transcript WebSocket — so the WS
    // stream is always empty for them and they must read from Firestore
    // regardless of the owner/guest routing. Soundshape devices broadcast on
    // WS, so guests keep reading the WS stream.
    if (useFirestore || license.type === 'microphonekit') {
      return this.createDatabaseListenerForLicense(license);
    }
    return this.transcriptStream.streamFor(licenseDeviceUuid(license));
  }

  /**
   * Timestamp (ms) to start listening from for a device. Captured once per
   * device on the first read and cached in-memory, so re-subscribes reuse it
   * and a reload (fresh service) starts from "now" instead of the backlog.
   */
  private cutoffFor(license: License): number {
    const key = license.firestoredb;
    let cutoff = this.listenCutoffByDb.get(key);
    if (cutoff === undefined) {
      cutoff = Date.now();
      this.listenCutoffByDb.set(key, cutoff);
    }
    return cutoff;
  }

  private createDatabaseListenerForLicense(license: License): Observable<Message[]> {
    switch (license.type) {
      case 'soundshape':
      case 'microphonekit':
        return this.db
          .collection(`devices/${license.firestoredb}/messages`, (ref) =>
            ref
              .orderBy('timestamp', 'desc')
              .where('timestamp', '>=', this.cutoffFor(license))
              .limit(25)
          )
          .valueChanges({ idField: 'documentID' }) as Observable<Message[]>;
      case 'autocaption': {
        const listener = this.db
          .collection(`autocaption/${license.firestoredb}/messages`, (ref) =>
            ref
              .orderBy('timestamp', 'desc')
              .where('timestamp', '>=', this.cutoffFor(license))
              .limit(25)
          )
          .valueChanges({ idField: 'documentID' }) as Observable<Message[]>;
        return listener.pipe(
          map((messages) => {
            messages.forEach((message) => {
              message.device = 'autocaption';
              message.micColor = { hex: '#2B2421', name: 'autocaption' };
            });
            return messages;
          })
        );
      }
      default:
        return of([]);
    }
  }

  // TODO: Remove once azure fixes this stuff
  private removeBannedMessages(changeHistory: ParsedMessage[], bannedDocumentIDs: Set<string>) {
    const bannedPrefix = /^ik wil graag (?:een|de|het|mijn|weten of|meer weten over)\b.*$/i;

    return changeHistory.filter(history => {
      const isFinalLowConf = history.is_final && history.confidence != null && history.confidence < 0.65;
      if (isFinalLowConf && bannedPrefix.test(history.fullMessage || '')) {
        const errorId = `banned_${history.documentID}`;
        if (!bannedDocumentIDs.has(errorId)) {
          bannedDocumentIDs.add(errorId);
          console.warn('Azure banned message (prefix regex)', history);
          Sentry.captureException(new Error('Azure banned message (prefix regex)'), {
            extra: { confidence: history.confidence }
          });
        }
        return false;
      }
      return true;
    });
  }

  public barcelonaBackendTranslations(licenses: License[]): Observable<Message[]> {
    this._messages.next([]);
    this._activeMics.next([]);

    const mutedMessageIds = new Set<number>();
    const listeners: Observable<Message[]>[] = [];
    for (const license of licenses) {
      // WS-only path preserved for the backend-translations flow (proxy devices).
      listeners.push(this.buildLicenseListener(license, false));
    }

    this.dbData = combineLatest(listeners).pipe(
      map((messagesProviders) =>
        messagesProviders.reduce((a, b) => [...a, ...b], [])
      )
    );

    return this.dbData
  }
}
